import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import Header from '../../components/ui/Header';
import MobileBottomNav from '../../components/ui/MobileBottomNav';
import Button from '../../components/ui/Button';
import ChatMessage from './components/ChatMessage';
import SessionTypeSelector from './components/SessionTypeSelector';
import QuickPrompts from './components/QuickPrompts';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { openaiService } from '../../services/openaiService';

const AICoaching = () => {
  const navigate = useNavigate();
  const { user, currentTenant } = useAuth();
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [sessionType, setSessionType] = useState('general');
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState([]);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    if (currentTenant?.id) {
      loadHistory();
    }
  }, [currentTenant]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef?.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadHistory = async () => {
    try {
      const { data, error } = await supabase
        ?.from('ai_coaching_sessions')
        ?.select('*')
        ?.eq('user_id', user?.id)
        ?.eq('tenant_id', currentTenant?.id)
        ?.order('created_at', { ascending: false })
        ?.limit(10);

      if (error) throw error;
      setHistory(data || []);
    } catch (error) {
      console.error('Error loading history:', error);
    }
  };

  const handleSendMessage = async () => {
    if (!inputMessage?.trim() || loading) return;

    const userMessage = inputMessage?.trim();
    setInputMessage('');
    
    setMessages(prev => [...prev, {
      role: 'user',
      content: userMessage,
      timestamp: new Date()
    }]);

    setLoading(true);

    try {
      const result = await openaiService?.getFitnessCoaching(
        userMessage,
        sessionType,
        { session_type: sessionType }
      );

      if (result?.success) {
        const aiMessage = {
          role: 'assistant',
          content: result?.response,
          timestamp: new Date()
        };

        setMessages(prev => [...prev, aiMessage]);

        await supabase?.from('ai_coaching_sessions')?.insert({
          tenant_id: currentTenant?.id,
          user_id: user?.id,
          session_type: sessionType,
          user_message: userMessage,
          ai_response: result?.response,
          context_data: { session_type: sessionType }
        });

        await loadHistory();
      } else {
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: `I apologize, but I encountered an error: ${result?.error}. Please try again.`,
          timestamp: new Date(),
          isError: true
        }]);
      }
    } catch (error) {
      console.error('Error sending message:', error);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: 'I apologize, but I encountered an unexpected error. Please try again.',
        timestamp: new Date(),
        isError: true
      }]);
    } finally {
      setLoading(false);
    }
  };

  const handleQuickPrompt = (prompt) => {
    setInputMessage(prompt);
  };

  const handleKeyPress = (e) => {
    if (e?.key === 'Enter' && !e?.shiftKey) {
      e?.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Header />
      
      <main className="flex-1 container mx-auto px-4 py-6 pb-24 md:pb-6 flex flex-col">
        <div className="mb-6">
          <h1 className="text-3xl font-heading font-bold text-foreground mb-2">
            AI Fitness Coach
          </h1>
          <p className="text-muted-foreground">
            Get personalized fitness guidance powered by AI
          </p>
        </div>

        <SessionTypeSelector
          selectedType={sessionType}
          onTypeChange={setSessionType}
        />

        <div className="flex-1 bg-card border border-border rounded-lg shadow-md flex flex-col overflow-hidden">
          <div className="flex-1 overflow-y-auto p-4 md:p-6 space-y-4">
            {messages?.length === 0 ? (
              <div className="h-full flex flex-col items-center justify-center text-center p-8">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-4">
                  <Icon name="Bot" size={32} className="text-primary" />
                </div>
                <h3 className="text-xl font-heading font-semibold text-foreground mb-2">
                  Welcome to AI Coaching!
                </h3>
                <p className="text-muted-foreground mb-6 max-w-md">
                  Ask me anything about workouts, nutrition, form checks, or motivation. I'm here to help you reach your fitness goals.
                </p>
                <QuickPrompts onSelectPrompt={handleQuickPrompt} sessionType={sessionType} />
              </div>
            ) : (
              <>
                {messages?.map((message, index) => (
                  <ChatMessage key={index} message={message} />
                ))}
                {loading && (
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Icon name="Loader2" size={20} className="animate-spin" />
                    <span>AI is thinking...</span>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </>
            )}
          </div>

          <div className="border-t border-border p-4">
            <div className="flex gap-2">
              <textarea
                value={inputMessage}
                onChange={(e) => setInputMessage(e?.target?.value)}
                onKeyPress={handleKeyPress}
                placeholder="Ask your fitness question..."
                rows={2}
                disabled={loading}
                className="flex-1 px-4 py-2 bg-background border border-border rounded-lg text-foreground resize-none focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50"
              />
              <Button
                onClick={handleSendMessage}
                disabled={!inputMessage?.trim() || loading}
                iconName="Send"
                iconSize={20}
                className="self-end"
                aria-label="Send message"
              />
            </div>
          </div>
        </div>
      </main>

      <MobileBottomNav />
    </div>
  );
};

export default AICoaching;
